import type { FetchError } from 'ofetch';

export const useUsersStore = defineStore('users', () => {
  const { getUsers, updateUserRoles: updateUserRolesViaApi, updateUserGroups: updateUserGroupsViaApi } = useApi();
  const rolesStore = useRolesStore();
  const { roles } = storeToRefs(rolesStore);
  const { fetchRoles } = rolesStore;

  const users = ref<UserWithRoles[]>([]);
  const usersPage = ref(1);
  const usersPerPage = ref(10);
  const usersSortBy = ref<PaginationSortBy>({
    key: 'last_name',
    order: 'asc',
  });
  const usersCount = ref(0);
  const usersLoading = ref(false);
  const userFilterKeys = ['email', 'email__icontains', 'email__startswith', 'role'];

  /**
   * Fetches a page of the users from the server and populates the store.
   * If the `forceRefresh` argument is set to true, then the related roles
   * are always re-requested. Otherwise, the last retrieved value will be
   * returned if one exists.
   *
   * @param options.page
   * @param options.itemsPerPage
   * @param options.sortBy
   * @param forceRefresh
   */
  const fetchUsers = async (options: DataTableOptions & UserFilters, forceRefresh = false) => {
    // TODO options diffing for proper forceRefresh logic (should only need forcing if the request uses the same options twice)
    usersLoading.value = true;

    const {
      page,
      itemsPerPage,
      sortBy,
    } = options;

    // Update page and itemsPerPage from options provided
    usersPage.value = page;
    usersPerPage.value = itemsPerPage;

    const apiOptions: PageFilters & UserFilters = {
      offset: (page - 1) * itemsPerPage,
      limit: itemsPerPage,
      ...filterEntries(options, ([key]) => userFilterKeys.includes(key)),
    };

    /**
     * Convert the first VDataTableServer sortBy option into Django's "order_by" param
     * and store the sortBy option provided.
     */
    if (sortBy[0]) {
      apiOptions.order_by = `${sortBy[0].order === 'desc' ? '-' : ''}${sortBy[0].key}`;
      usersSortBy.value = sortBy[0];
    }

    // Request the users and roles at the same time
    const [_, { count, results }] = await Promise.all([
      fetchRoles(forceRefresh),
      getUsers(apiOptions),
    ]);
    usersCount.value = count;

    // Assign full role objects to each of the user objects according to their role ids
    users.value = results.map(user => {
      return {
        ...user,
        role_ids: user.roles,
        roles: roles.value.filter(role => user.roles.includes(role.id)),
      };
    });
    usersLoading.value = false;
  };

  /**
   * Sets the roles for the given user.
   *
   * @param roleId
   * @param userId
   */
  const updateUserRoles = async (userId: number, roleIds: number[]) => {
    // Update the user record with the modified roles
    try {
      await updateUserRolesViaApi(userId, roleIds);
    }
    catch (err) {
      throw new Error((err as FetchError).data.detail);
    }
  };

  /**
   * Sets the groups for the given user.
   *
   * @param groupId
   * @param userId
   */
  const updateUserGroups = async (userId: number, groupIds: number[]) => {
    // Update the user record with the modified groups
    try {
      await updateUserGroupsViaApi(userId, groupIds);
    }
    catch (err) {
      throw new Error((err as FetchError).data.detail);
    }
  };

  return {
    users,
    usersPage,
    usersPerPage,
    usersSortBy,
    usersCount,
    usersLoading,
    fetchUsers,
    updateUserRoles,
    updateUserGroups,
  };
});
